<template>
  <ul v-if="currencies" class="list">
    <li
      v-for="currency in currencies"
      :key="currency.code"
      :class="{ selected: isCurrentCurrency(currency) }"
      data-allow-mismatch="class"
      @click.prevent="setCurrency(currency.code, $event)"
    >
      <span class="currency-symbol">{{ currency.symbol }}</span>
      <span class="currency-name">{{ currency.name }}</span>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { useRoute } from 'nuxt/app'

interface CurrencyResponse {
  code: string
  name: string
  symbol: string
  rate: number
}

const emit = defineEmits<(e: 'click', $event: Event) => void>()
const route = useRoute()
const lang = route.params.lang || 'en-gb'
const currencyStore = useCurrencyStore(usePinia())
const userDataStore = useUserDataStore(usePinia())

const { data: currencies } = await useAsyncData(
  `currencies-${lang}`,
  loadCurrencies,
  // avoid duplicate requests server and client
  {
    server: true,
    lazy: process.client, // Defer fetching until client-side execution
    dedupe: 'cancel',
    getCachedData: (key, nuxt) =>
      nuxt.payload.data[key] as CurrencyResponse[] | undefined,
  },
)

async function loadCurrencies() {
  await currencyStore.load()
  return Object.values(currencyStore.currencies)
}

function isCurrentCurrency(currency: CurrencyResponse) {
  return currencyStore.currentCurrencyCode === currency.code
}

async function setCurrency(currencyCode: string, $event: Event) {
  currencyStore.setCurrentCurrency(currencyCode)
  await userDataStore.setCurrency(currencyCode)
  emit('click', $event)
}
</script>

<style scoped>
.currency-symbol {
  width: 2.8em;
  margin-right: 0.8em;
  font-weight: bold;
  text-align: right;
}

.currency-name {
  flex: 1;
}
</style>
